/**
 * What fits what (design D3). A leg fits a dog body because of what a leg *is* — this is
 * the whole rule, as data, so a later assembly (a lock and a key, say) is another row in
 * `ASSEMBLIES` rather than a second copy of the logic below it.
 *
 * Pure: plain data in, plain data out. No three.js, no Rapier, no React and no store
 * import — `src/game/` is renderer-agnostic and headlessly testable, architectural rule 1.
 * Offsets are plain `Vec3`, the same kind of geometry `objectKinds.ts` already holds as
 * `size`; the engine turns an offset into a `position` prop and nothing more.
 */

import type { Pocket } from './pocket';
import type { ObjectKind, Vec3 } from './profile/types';

/** Where a part sits on its host, in the host's local space. */
export type Socket = { readonly id: string; readonly offset: Vec3 };

/** One host kind's assembly: what fits in it, and where. */
export type AssemblySpec = {
  readonly partKind: ObjectKind;
  readonly sockets: readonly Socket[];
};

/**
 * One attached part: which host it is on, and which of the host's sockets it fills.
 * Keyed by the *part's* id in `Attachments` below — not the host's — because a part is
 * unique (one leg, one socket) while a host may one day hold several parts of the same
 * kind, and the id that never repeats is the part's.
 */
export type Attachment = { readonly hostId: string; readonly socketId: string };

/** Every attached part in the run, keyed by the part's object id. */
export type Attachments = Readonly<Record<string, Attachment>>;

/**
 * The dog body's four sockets, one at each shoulder and haunch, in a fixed order:
 * front-left, front-right, back-left, back-right. The order is what makes `nextSocket`
 * deterministic — the first empty leg found on the body is always the same one, run to run.
 *
 * Offsets are host-local, and they are where the *hip pivots* sit, not where the feet do:
 * a leg hangs down from its socket, so the socket belongs inside the torso at about the
 * height a real hip is, and the leg's own length is what reaches the ground from there.
 * The numbers follow from the two boxes in `objectKinds.ts` and the proportions `DogLeg`
 * draws at. A leg box is 0.18 high and `DogLeg` spends all of it on thigh and shin, plus a
 * paw that hangs a further ~0.04 below the shin — about 0.218 from hip to sole. The body
 * box is 0.42 high, so its underside is at y = -0.21, and a hip at y = 0.008 is exactly the
 * height that stands the soles on it. That is what design D2 meant by sizing the box for
 * the whole standing dog: with these offsets a finished dog's feet meet the surface its
 * box rests on instead of sinking through it.
 *
 * x and z keep each leg within the torso capsule's own footprint (radius 0.143, half-length
 * 0.184) so the thigh emerges from the body rather than floating clear of it.
 */
const DOG_LEG_SOCKETS: readonly Socket[] = [
  { id: 'front-left', offset: { x: -0.1, y: 0.008, z: -0.13 } },
  { id: 'front-right', offset: { x: 0.1, y: 0.008, z: -0.13 } },
  { id: 'back-left', offset: { x: -0.1, y: 0.008, z: 0.13 } },
  { id: 'back-right', offset: { x: 0.1, y: 0.008, z: 0.13 } },
];

/**
 * What can be attached to what. A host kind absent from this table cannot be attached to
 * at all — that is how a table, a screwdriver or a loose leg all refuse a leg, with no
 * special-casing beyond "not a host".
 */
export const ASSEMBLIES: Partial<Record<ObjectKind, AssemblySpec>> = {
  'robot-dog-body': {
    partKind: 'robot-dog-leg',
    sockets: DOG_LEG_SOCKETS,
  },
};

/** The attachments belonging to one host, in no particular order. */
export function attachedTo(attachments: Attachments, hostId: string): readonly Attachment[] {
  return Object.values(attachments).filter((attachment) => attachment.hostId === hostId);
}

/**
 * The next empty socket a host has, in `ASSEMBLIES`' fixed order, or `null` when the host
 * is not an assembly at all or every socket is already filled.
 */
export function nextSocket(
  hostKind: ObjectKind,
  attachments: Attachments,
  hostId: string,
): Socket | null {
  const spec = ASSEMBLIES[hostKind];
  if (!spec) return null;

  const filled = new Set(attachedTo(attachments, hostId).map((attachment) => attachment.socketId));
  return spec.sockets.find((socket) => !filled.has(socket.id)) ?? null;
}

/**
 * Whether the thing in the player's hands can attach to the thing under the crosshair:
 * the target must be a known host, the carried kind must be that host's part, and the
 * host must have a socket free.
 */
export function canAttach(
  carriedKind: ObjectKind,
  targetKind: ObjectKind,
  attachments: Attachments,
  targetId: string,
): boolean {
  const spec = ASSEMBLIES[targetKind];
  if (!spec || spec.partKind !== carriedKind) return false;

  return nextSocket(targetKind, attachments, targetId) !== null;
}

/** Whether every socket a host has is filled. */
export function isComplete(hostKind: ObjectKind, attachments: Attachments, hostId: string): boolean {
  const spec = ASSEMBLIES[hostKind];
  if (!spec) return false;

  return attachedTo(attachments, hostId).length >= spec.sockets.length;
}

/**
 * Every kind that is part of an assembly, one way or the other: a host kind (the dog body)
 * or the kind that fits it (a leg). Built once, from `ASSEMBLIES`, rather than a hardcoded
 * list — a second row in that table is a second host and a second part kind for free.
 */
const PART_KINDS: ReadonlySet<ObjectKind> = new Set(
  Object.entries(ASSEMBLIES).flatMap(([hostKind, spec]) => [
    hostKind as ObjectKind,
    ...(spec ? [spec.partKind] : []),
  ]),
);

/** Whether `kind` is any part of any assembly — a host or the part that fits one. */
export function isPartKind(kind: ObjectKind): boolean {
  return PART_KINDS.has(kind);
}

/**
 * The store fields D1's "loose" question needs: who is carrying, what is pocketed, and
 * what is already attached. Plain data the store already holds under these exact names —
 * this is not a copy of the store, just the shape of the question.
 */
export type PartState = {
  readonly carriedObjectId: string | null;
  readonly pocket: Pocket;
  readonly attachments: Attachments;
};

/**
 * Whether the object `id` — of kind `kind` — is lying loose in the world: not carried, not
 * in a pocket slot, not attached to a host. A kind that is not a part of any assembly is
 * never loose, regardless of where it is (design D1: nothing that is not a part raises a
 * beacon or counts toward the tally).
 */
export function isPartLoose(id: string, kind: ObjectKind, state: PartState): boolean {
  if (!isPartKind(kind)) return false;
  if (state.carriedObjectId === id) return false;
  if (state.pocket.some((item) => item?.id === id)) return false;
  if (state.attachments[id] !== undefined) return false;
  return true;
}

/**
 * Whether the player has this part to hand: held, pocketed, attached to a host — or a host
 * that has parts on it, which means they found it and built on it.
 *
 * This is deliberately *not* the negation of `isPartLoose`. A finished dog set down on a
 * table is still lying in the world, so it still raises its beacon — that is how a player
 * walks back to the dog they left behind — but there is nothing about it left to find, and
 * a tally reading "4 of 5" beside it would send them back into the maze after a part that
 * is standing in front of them.
 */
export function isPartAccountedFor(id: string, kind: ObjectKind, state: PartState): boolean {
  if (!isPartLoose(id, kind, state)) return true;
  return attachedTo(state.attachments, id).length > 0;
}

/** One object as far as the tally cares: its id and its kind. */
export type PartLike = { readonly id: string; readonly kind: ObjectKind };

/**
 * How many of the level's parts the player has accounted for — held, pocketed, attached,
 * or built on — out of how many the level holds in total. A level with no parts at all reports a total
 * of 0, which is the HUD's cue to show nothing (design D4).
 */
export function tallyParts(
  objects: readonly PartLike[],
  state: PartState,
): { found: number; total: number } {
  const parts = objects.filter((object) => isPartKind(object.kind));
  const found = parts.filter((part) => isPartAccountedFor(part.id, part.kind, state)).length;
  return { found, total: parts.length };
}
